import type { Entry } from './types';

/**
 * Score a single query token against a text field.
 * Tiers: exact prefix > word-boundary substring > substring > in-order
 * subsequence with a gap penalty (so 'pgsmth' still finds 'pagesmith').
 * Subsequence matching only makes sense on short fields like names — on long
 * prose almost any token matches by scattered letters — so it's opt-out.
 * Returns 0 when the token doesn't match at all.
 */
export function matchScore(token: string, text: string, allowSubsequence = true): number {
  if (!token || !text) return 0;
  if (text.startsWith(token)) return 4;
  const idx = text.indexOf(token);
  if (idx > 0) {
    return /[a-z0-9]/.test(text[idx - 1]) ? 2 : 3;
  }
  if (!allowSubsequence) return 0;
  let ti = 0;
  let gaps = 0;
  let last = -1;
  for (let i = 0; i < text.length && ti < token.length; i++) {
    if (text[i] === token[ti]) {
      if (last >= 0 && i > last + 1) gaps++;
      last = i;
      ti++;
    }
  }
  if (ti === token.length) return Math.max(0.5, 1.5 - gaps * 0.25);
  return 0;
}

/**
 * Fuzzy-score a whole query against an entry. Every whitespace-separated
 * token must match somewhere (AND semantics); each token contributes its
 * best weighted field score. Returns 0 when any token fails to match.
 */
export function fuzzyScore(query: string, entry: Entry): number {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return 1;
  const fields = [
    { text: entry.name.toLowerCase(), weight: 3, subsequence: true },
    { text: entry.slug.toLowerCase(), weight: 2.5, subsequence: true },
    {
      // A product is also found by how it is paid for and where it runs ("free", "android").
      text: [entry.category, ...entry.tags, entry.product?.pricing ?? '', ...(entry.product?.platforms ?? [])]
        .join(' ')
        .toLowerCase(),
      weight: 2,
      subsequence: false,
    },
    { text: entry.description.toLowerCase(), weight: 1, subsequence: false },
  ];
  let total = 0;
  for (const token of tokens) {
    let best = 0;
    for (const f of fields) {
      const s = matchScore(token, f.text, f.subsequence) * f.weight;
      if (s > best) best = s;
    }
    if (best === 0) return 0;
    total += best;
  }
  return total;
}

/**
 * Rank entries against a query: score desc, with an optional comparator
 * as the tiebreak (the active sort order). Empty query returns the input
 * order unchanged.
 */
export function searchEntries(
  query: string,
  entries: Entry[],
  tiebreak?: (a: Entry, b: Entry) => number,
): Entry[] {
  const q = query.trim();
  if (!q) return entries;
  return entries
    .map((e) => ({ e, s: fuzzyScore(q, e) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || (tiebreak ? tiebreak(a.e, b.e) : 0))
    .map((x) => x.e);
}
